import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { setUserId } from "../../Redux/actions";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { styled } from "@mui/system";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import colors from "colors/color";

const StyledCard = styled(Card)(({ theme }) => ({
  width: {
    xs: "100%",
    md: "50%",
  },
  height: "auto",
  minHeight: 300,
  marginBottom: theme.spacing(2),
  borderRadius: "20px",
  boxShadow: "0 4px 40px 0px #0000001A",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    cursor: "pointer",
    boxShadow: `0 0px 15px 0px ${colors.primary}`,
    transition: "all 0.5s ease-in-out",
  },
  [theme.breakpoints.up("sm")]: {
    maxWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(3),
  },
}));

const TimeChip = styled(Chip)(({ theme, chipColor }) => ({
  position: "absolute",
  top: theme.spacing(5),
  right: theme.spacing(3),
  borderRadius: "10px",
  padding: "0px 12px",
  height: "32px",
  backgroundColor: "#fff",
  boxShadow: "0 4px 40px 0px #0000001A",
  color: chipColor,
  "& .MuiChip-label": {
    padding: "0px",
    fontSize: 12,
    fontWeight: 500,
  },
  [theme.breakpoints.down("sm")]: {
    right: theme.spacing(2),
    top: theme.spacing(4),
  },
}));

const PostedTime = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  color: "rgba(0, 0, 0, 0.6)",
  fontSize: theme.typography.pxToRem(14),
  marginTop: "4px",
}));
function JobCard({
  title = "Data scientist",
  company = "PULSE.digital Maroc",
  localisation = "Marrakech · Morocco",
  description = "We are actively looking for a talented Data scientist to join our dynamic team! Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam mollis placerat lacus, et egestas metus mattis a. Praesent dapibus mauris...",
  deadline = "3 days left",
  post_id,
  refresh,
  recruiter,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  const { timeLeft, chipColor } = React.useMemo(() => {
    const now = dayjs();
    const endDate = dayjs(deadline);
    const daysLeft = endDate.diff(now, "day");

    let chipColor = "#34A853";

    if (daysLeft < 0) {
      return {
        timeLeft: t("myCard.timeLeft.deadlinePassed"),
        chipColor: "#FF0000",
      };
    } else if (daysLeft === 1 || daysLeft === 0) {
      chipColor = daysLeft >= 1 && daysLeft < 3 ? "#FF0000" : "#FFA500";
      return { timeLeft: t("myCard.timeLeft.oneDayLeft"), chipColor };
    } else if (daysLeft < 3) {
      chipColor = "#FFA500";
      return {
        timeLeft:
          daysLeft + " " + t("myCard.timeLeft.daysLeft").replace("{days}", ""),
        chipColor,
      };
    } else if (daysLeft < 7) {
      return {
        timeLeft:
          daysLeft + " " + t("myCard.timeLeft.daysLeft").replace("{days}", ""),
        chipColor,
      };
    } else if (endDate.diff(now, "week") === 1) {
      return { timeLeft: t("myCard.timeLeft.oneWeekLeft"), chipColor };
    } else if (endDate.diff(now, "week") < 4) {
      const weeksCount = endDate.diff(now, "week");
      return {
        timeLeft:
          weeksCount +
          " " +
          t("myCard.timeLeft.weeksLeft").replace("{weeks}", ""),
        chipColor,
      };
    } else if (endDate.diff(now, "month") === 1) {
      return { timeLeft: t("myCard.timeLeft.oneMonthLeft"), chipColor };
    } else {
      const monthsCount = endDate.diff(now, "month");
      return {
        timeLeft:
          monthsCount +
          " " +
          t("myCard.timeLeft.monthsLeft").replace("{months}", ""),
        chipColor,
      };
    }
  }, [deadline, t]);

  const cutText = (text, length) => {
    if (text && typeof text === "string") {
      if (text.length > length) {
        return `${text.substr(0, length)}...`;
      }
    }
    return text || "";
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <TimeChip label={timeLeft} chipColor={chipColor} />
        <CardHeader
          title={
            <Typography
              variant="h5"
              sx={{
                color: "#004F98",
                fontWeight: 600,
                fontSize: {
                  xs: "18px",
                  sm: "20px",
                  md: "24px",
                },
                mb: 1,
                fontFamily: "Poppins",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {cutText(title, 30)}
            </Typography>
          }
          subheader={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <LocationOnIcon
                sx={{
                  fontSize: {
                    xs: "16px",
                    sm: "18px",
                  },
                  color: "rgba(0, 0, 0, 0.6)",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: {
                    xs: "12px",
                    sm: "14px",
                  },
                  fontFamily: "Poppins",
                }}
              >
                {localisation}
              </Typography>
            </Box>
          }
        />

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Divider
            sx={{
              width: "90%",
              background: "#004F9833",
              mt: { xs: 0.5, sm: 1 },
            }}
          />
        </Box>

        <Box sx={{ mb: 3, p: { xs: 1, sm: 2 } }}>
          <Typography
            variant="h6"
            sx={{
              color: "#004F98",
              fontSize: {
                xs: "16px",
                sm: "20px",
                md: "22px",
              },
              mb: { xs: 1, sm: 2 },
              fontWeight: 700,
              fontFamily: "Poppins",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {company}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "rgba(0, 0, 0, 0.7)",
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              lineHeight: 1.6,
              fontFamily: "Poppins",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );
}

const mapStateToProps = (state) => ({
  userId: state.userId,
});

const mapDispatchToProps = {
  setUserId,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobCard);
